import DSBox from "views/system-design/DSBox";
import MultiSelectFilter from "components/MultiSelectFilter";
import firebaseService from "firebase_service/firebaseService";
import { AlertPolicy } from "../../../api/alertPolicies";
import { KlydoMonitor, MonitorStatus } from "../../../api/admin";

// Sentinel filter value matching monitors without an alert policy. Kept
// module-private so the null/""-to-sentinel mapping cannot be forgotten by
// callers — use matchesMonitorFilters instead.
const NO_POLICY = "no-policy";

export interface MonitorFiltersState {
  statuses: MonitorStatus[];
  alertPolicyIds: string[];
}

// Fresh object per call: this value becomes React state, and returning a
// shared module constant would alias state with the module default.
const emptyMonitorFilters = (): MonitorFiltersState => ({
  statuses: [],
  alertPolicyIds: [],
});

// Filters are per admin — two users sharing a browser profile must not
// inherit each other's view of the fleet.
const getFiltersStorageKey = () => {
  const userId = firebaseService.getUserId();
  if (!userId) return null;
  return `klydo-monitor-filters-${userId}`;
};

const MONITOR_STATUSES = Object.values(MonitorStatus);

// Stored JSON is untrusted (old shape, manual edits) — validate each field
// and fall back to empty rather than crashing the view.
export const loadStoredMonitorFilters = (): MonitorFiltersState => {
  try {
    const key = getFiltersStorageKey();
    if (!key) return emptyMonitorFilters();
    const raw = localStorage.getItem(key);
    if (!raw) return emptyMonitorFilters();
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return emptyMonitorFilters();
    const { statuses, alertPolicyIds } = parsed as Record<string, unknown>;
    return {
      statuses: Array.isArray(statuses)
        ? statuses.filter((status): status is MonitorStatus =>
            MONITOR_STATUSES.includes(status as MonitorStatus),
          )
        : [],
      alertPolicyIds: Array.isArray(alertPolicyIds)
        ? alertPolicyIds.filter((id): id is string => typeof id === "string")
        : [],
    };
  } catch {
    return emptyMonitorFilters();
  }
};

export const storeMonitorFilters = (filters: MonitorFiltersState) => {
  try {
    const key = getFiltersStorageKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(filters));
  } catch {
    // Storage full or blocked — filtering still works for this session.
  }
};

// Drop stored ids of policies that no longer exist (deleted since the filter
// was saved): a selected id without a menu row could never be unticked, so it
// would filter the table forever. Returns the input object when nothing
// changed so setState callers can bail out.
export const pruneMonitorFilters = (
  filters: MonitorFiltersState,
  policies: AlertPolicy[],
): MonitorFiltersState => {
  const validIds = new Set<string>([
    NO_POLICY,
    ...policies.map((policy) => policy.id),
  ]);
  const alertPolicyIds = filters.alertPolicyIds.filter((id) =>
    validIds.has(id),
  );
  return alertPolicyIds.length === filters.alertPolicyIds.length
    ? filters
    : { ...filters, alertPolicyIds };
};

// An empty selection means "all" for each filter. A falsy alertPolicyId
// (null or "") counts as "No policy", matching the table cell's truthy check.
export const matchesMonitorFilters = (
  monitor: KlydoMonitor,
  filters: MonitorFiltersState,
) =>
  (filters.statuses.length === 0 ||
    filters.statuses.includes(monitor.status)) &&
  (filters.alertPolicyIds.length === 0 ||
    filters.alertPolicyIds.includes(monitor.alertPolicyId || NO_POLICY));

const STATUS_OPTIONS = MONITOR_STATUSES.map((status) => ({
  value: status,
  label: status.charAt(0).toUpperCase() + status.slice(1),
}));

const MonitorFilters = ({
  filters,
  onFiltersChange,
  policies,
}: {
  filters: MonitorFiltersState;
  onFiltersChange: (filters: MonitorFiltersState) => void;
  policies: AlertPolicy[];
}) => {
  const policyOptions = [
    { value: NO_POLICY, label: "No policy" },
    ...policies.map((policy) => ({ value: policy.id, label: policy.name })),
  ];

  return (
    <DSBox sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
      <MultiSelectFilter
        label="Status"
        selected={filters.statuses}
        options={STATUS_OPTIONS}
        allLabel="All Statuses"
        onChange={(statuses) => onFiltersChange({ ...filters, statuses })}
      />
      <MultiSelectFilter
        label="Alert Policy"
        selected={filters.alertPolicyIds}
        options={policyOptions}
        allLabel="All Policies"
        onChange={(alertPolicyIds) =>
          onFiltersChange({ ...filters, alertPolicyIds })
        }
      />
    </DSBox>
  );
};

export default MonitorFilters;
